import type { TrackingMetadata } from "@/types";

const TRACKING_STORAGE_KEY = "vd_tracking_metadata";
const LANDING_URL_PARAM = "landing_url";
const LANDING_REFERRER_PARAM = "landing_referrer";
const CAPTURED_AT_PARAM = "tracking_captured_at";

export type StoredTrackingMetadata = {
  utmParameters?: Record<string, string>;
  landingUrl?: string;
  landingReferrer?: string;
  trackingCapturedAt?: string;
};

const parseStoredMetadata = (): StoredTrackingMetadata | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(TRACKING_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as StoredTrackingMetadata;
  } catch (error) {
    console.error("Failed to parse stored tracking metadata", error);
    return null;
  }
};

const persistMetadata = (metadata: StoredTrackingMetadata) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(metadata));
  } catch (error) {
    console.error("Failed to persist tracking metadata", error);
  }
};

const safeDecode = (value: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const mergeMetadata = (
  existing: StoredTrackingMetadata | null,
  incoming: StoredTrackingMetadata,
): StoredTrackingMetadata => {
  if (!existing) {
    return incoming;
  }

  return {
    ...existing,
    ...incoming,
    utmParameters:
      Object.keys(incoming.utmParameters ?? {}).length > 0
        ? incoming.utmParameters
        : existing.utmParameters,
  };
};

export const captureTrackingFromUrl = () => {
  if (typeof window === "undefined") {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);

  const utmParameters: Record<string, string> = {};
  urlParams.forEach((value, key) => {
    if (key.toLowerCase().startsWith("utm_") && value) {
      utmParameters[key] = value;
    }
  });

  const landingUrl = safeDecode(urlParams.get(LANDING_URL_PARAM));
  const landingReferrer =
    safeDecode(urlParams.get(LANDING_REFERRER_PARAM)) ||
    (document.referrer ? document.referrer : undefined);
  const trackingCapturedAt = urlParams.get(CAPTURED_AT_PARAM) || undefined;

  const nextMetadata: StoredTrackingMetadata = {};

  if (Object.keys(utmParameters).length > 0) {
    nextMetadata.utmParameters = utmParameters;
  }
  if (landingUrl) {
    nextMetadata.landingUrl = landingUrl;
  }
  if (landingReferrer) {
    nextMetadata.landingReferrer = landingReferrer;
  }
  if (trackingCapturedAt) {
    nextMetadata.trackingCapturedAt = trackingCapturedAt;
  }

  if (Object.keys(nextMetadata).length === 0) {
    return;
  }

  const merged = mergeMetadata(parseStoredMetadata(), nextMetadata);
  persistMetadata(merged);
};

export const getTrackingMetadataPayload = (): TrackingMetadata | undefined => {
  const stored = parseStoredMetadata();
  if (!stored) {
    return undefined;
  }

  const payload: TrackingMetadata = {};
  if (stored.utmParameters && Object.keys(stored.utmParameters).length > 0) {
    payload.utmParameters = stored.utmParameters;
  }
  if (stored.landingUrl) {
    payload.landingUrl = stored.landingUrl;
  }
  if (stored.landingReferrer) {
    payload.landingReferrer = stored.landingReferrer;
  }
  if (stored.trackingCapturedAt) {
    payload.trackingCapturedAt = stored.trackingCapturedAt;
  }

  return Object.keys(payload).length > 0 ? payload : undefined;
};
